import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const MenuCarousel: React.FC = () => {
  const { t } = useTranslation();
  const menus = [
    {
      title: "Menu Principal",
      image: "/restaurant_photos/menu1.png"
    },
    {
      title: "Spécialités", 
      image: "/restaurant_photos/menu2.png"
    },
    {
      title: "Carte Complète",
      image: "/restaurant_photos/menu3.png"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % menus.length);
    }, 10000);

    return () => clearInterval(timer);
  }, [menus.length]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? menus.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % menus.length);
  };

  return (
    <section className="py-8 md:py-12 lg:py-16 bg-luxury-black">
      <div className="section-padding">
        <div className="text-center mb-8 md:mb-12">
          <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">
            {t('menuCarousel.title')}
          </p>
          <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-champagne">
            {t('menuCarousel.viewMenus')}
          </h2>
          <div className="h-px bg-luxury-gold mx-auto w-20" />
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="relative h-[400px] md:h-[600px] lg:h-[800px] bg-luxury-black/50 rounded-lg overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <img
                  src={menus[currentIndex].image}
                  alt={menus[currentIndex].title}
                  className="w-full h-full object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-luxury-black/80 to-transparent p-8">
                  <h3 className="text-3xl font-display text-luxury-champagne text-center">
                    {menus[currentIndex].title}
                  </h3>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-luxury-champagne/70 hover:text-luxury-gold transition-colors duration-300"
            >
              <ChevronLeft size={40} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-luxury-champagne/70 hover:text-luxury-gold transition-colors duration-300"
            >
              <ChevronRight size={40} />
            </button>
          </div>

          {/* Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {menus.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-luxury-gold' 
                    : 'w-2 bg-luxury-champagne/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MenuCarousel;